import { type FC, type ReactNode } from 'react';
import classnames from 'classnames';
import { useIntl, type MessageDescriptor } from 'dibs-react-intl';
import { type HeadingType } from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { OrderedList } from 'dibs-elements/exports/OrderedList.tsx';
import {
    ResponsiveExpandingContentProvider,
    ResponsiveExpandingContentTrigger,
    useResponsiveExpandingContent,
} from './ResponsiveExpandingContent.tsx';
import styles from './FormattedExpandingList.scss';

// this is a separate component bc useResponsiveExpandingContent must be scoped
// to a child of ResponsiveExpandingContentProvider.
const List: FC<{ children: ReactNode }> = ({ children }) => {
    const [ref, { style, className }] = useResponsiveExpandingContent();
    return (
        <OrderedList ref={ref} style={style} className={classnames(styles.list, className)}>
            {children}
        </OrderedList>
    );
};

export const FormattedExpandingList: FC<{
    Heading: HeadingType;
    headingDescriptor: MessageDescriptor;
    listDescriptor: MessageDescriptor;
    deviceType?: string;
}> = ({ Heading, headingDescriptor, listDescriptor, deviceType }) => {
    const { formatMessage } = useIntl();
    // formatMessage value-as-fn will throw React key errors if keys are not used
    let keyIndex = 0;
    return (
        <ResponsiveExpandingContentProvider deviceType={deviceType}>
            <Heading className={styles.listHeading}>
                <ResponsiveExpandingContentTrigger>
                    {formatMessage(headingDescriptor)}
                </ResponsiveExpandingContentTrigger>
            </Heading>
            <List>
                {formatMessage(listDescriptor, {
                    settings: msg => (
                        <Link key="settings-link" href="/nft/wallet/">
                            {msg}
                        </Link>
                    ),
                    span: msg => <span key="settings-span">{msg}</span>,
                    li: msg => (
                        <li className={styles.listItem} key={`li-${keyIndex++}`}>
                            {msg}
                        </li>
                    ),
                })}
            </List>
        </ResponsiveExpandingContentProvider>
    );
};
